import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon } from "lucide-react";
import { format, eachDayOfInterval, getDay } from "date-fns";
import { useContentStore, FREQUENCY_OPTIONS } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BatchPlanningModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

function generateSlotDates(
  startDate: Date,
  endDate: Date,
  preferredDays: number[],
  frequency: number
): Date[] {
  if (preferredDays.length === 0) return [];

  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  const eligible = allDays.filter((d) => preferredDays.includes(getDay(d)));

  // Distribute evenly: pick `frequency` slots per week
  const result: Date[] = [];
  let weekSlots = 0;
  let currentWeek = -1;

  for (const day of eligible) {
    const weekNum = Math.floor(
      (day.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
    );
    if (weekNum !== currentWeek) {
      currentWeek = weekNum;
      weekSlots = 0;
    }
    if (weekSlots < frequency) {
      result.push(day);
      weekSlots++;
    }
  }

  return result;
}

export function BatchPlanningModal({ isOpen, onClose, projectId }: BatchPlanningModalProps) {
  const createScheduleSlot = useContentStore((s) => s.createScheduleSlot);
  const { toast } = useToast();

  const [frequency, setFrequency] = useState("2");
  const [preferredDays, setPreferredDays] = useState<number[]>([1, 3, 5]); // Mon, Wed, Fri
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const toggleDay = (day: number) => {
    setPreferredDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const generatedDates = useMemo(
    () =>
      startDate && endDate
        ? generateSlotDates(startDate, endDate, preferredDays, parseInt(frequency))
        : [],
    [startDate, endDate, preferredDays, frequency]
  );

  const handleSubmit = async () => {
    if (generatedDates.length === 0) return;

    setSubmitting(true);
    try {
      await Promise.all(
        generatedDates.map((date) =>
          createScheduleSlot(projectId, format(date, "yyyy-MM-dd"))
        )
      );
      toast({ title: `${generatedDates.length} schedule slots created` });
      onClose();
    } catch {
      toast({ title: "Failed to create schedule", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Batch Schedule</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Frequency */}
          <div className="space-y-2">
            <Label htmlFor="batch-frequency" className="text-xs">Publish Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger id="batch-frequency" className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preferred days */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium">Preferred Days</legend>
            <div className="flex gap-2">
              {DAYS_OF_WEEK.map((d) => (
                <label
                  key={d.value}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <Checkbox
                    checked={preferredDays.includes(d.value)}
                    onCheckedChange={() => toggleDay(d.value)}
                    aria-label={d.label}
                  />
                  <span className="text-xs">{d.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    <span className="text-xs">{startDate ? format(startDate, "MMM dd") : "Start"}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    <span className="text-xs">{endDate ? format(endDate, "MMM dd") : "End"}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={(date) => date < (startDate || new Date())}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Preview */}
          {startDate && endDate && (
            <p className="text-sm text-muted-foreground">
              This creates <span className="font-medium text-foreground">{generatedDates.length}</span> scheduled slots.
            </p>
          )}

          {/* Submit */}
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!startDate || !endDate || generatedDates.length === 0 || submitting}
          >
            {submitting ? "Creating..." : `Create ${generatedDates.length} Slots`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
